/**
 * 논문 리포트 HTML 파싱 유틸리티
 * HTML 형식의 리포트를 파싱하여 카테고리 구조로 변환합니다.
 */

/**
 * HTML 형식의 리포트를 파싱합니다.
 * @param {string} htmlContent - HTML 형식의 리포트 내용
 * @returns {Object} 파싱된 리포트 데이터
 */
export const parseHtmlReport = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') {
        return { error: 'Invalid content', papers: [] };
    }

    try {
        // DOM Parser를 사용하여 HTML 파싱
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');

        const result = { header: null, categories: [], papers: [] };

        // 헤더 파싱
        const headerElement = doc.querySelector('.report-header');
        if (headerElement) {
            result.header = {
                title: getTextContent(headerElement.querySelector('.report-title')),
                date: getTextContent(headerElement.querySelector('.report-date')),
                agentInfo: getTextContent(headerElement.querySelector('.report-agent-info')),
            };
        }

        // 카테고리 파싱
        const categoryElements = doc.querySelectorAll('.category');
        for (const categoryElement of categoryElements) {
            const categoryData = { categories: [], papers: [] };

            // 카테고리 정보 추출
            const categoriesAttr = categoryElement.getAttribute('data-categories');
            if (categoriesAttr) {
                try {
                    categoryData.categories = JSON.parse(categoriesAttr);
                } catch (e) {
                    console.error('JSON parse error in categories:', e);
                }
            }

            // 카테고리 내 논문 파싱
            const paperElements = categoryElement.querySelectorAll('.paper');
            for (const paperElement of paperElements) {
                const paper = parsePaper(paperElement);
                if (paper) {
                    categoryData.papers.push(paper);
                    // 전체 논문 목록에도 추가
                    result.papers.push({ ...paper, categories: categoryData.categories });
                }
            }

            if (categoryData.categories.length > 0 || categoryData.papers.length > 0) {
                result.categories.push(categoryData);
            }
        }

        // 카테고리 없이 독립적인 논문이 있는 경우
        const standaloneElements = doc.querySelectorAll(':not(.category) > .paper');
        for (const paperElement of standaloneElements) {
            // 이미 카테고리 내에서 처리된 논문은 제외
            if (paperElement.parentElement && paperElement.parentElement.classList.contains('category')) {
                continue;
            }

            const paper = parsePaper(paperElement);
            if (paper) {
                result.papers.push(paper);
            }
        }

        return result;
    } catch (e) {
        console.error('Error parsing HTML report:', e);
        return { error: e.message, papers: [] };
    }
};

/**
 * 논문 요소를 파싱합니다.
 * @param {Element} paperElement - 논문 DOM 요소
 * @returns {Object} 파싱된 논문 데이터
 */
const parsePaper = (paperElement) => {
    try {
        const paper = {
            id: paperElement.getAttribute('data-paper-id') || '',
            title: getTextContent(paperElement.querySelector('.paper-title')),
            authors: getTextContent(paperElement.querySelector('.paper-authors')),
            summary: [],
            scores: {},
            link: '',
            tags: [],
        };

        // 요약 섹션 파싱
        const summaryElement = paperElement.querySelector('.paper-summary');
        if (summaryElement) {
            // 문제점, 해결책, 세부설명 등 섹션 파싱
            for (const sectionType of ['problem', 'solution', 'details']) {
                const sectionElement = summaryElement.querySelector(`.${sectionType}-section`);
                if (sectionElement) {
                    // 제목과 내용 분리
                    const strongElement = sectionElement.querySelector('strong');
                    if (strongElement) {
                        const sectionTitle = strongElement.textContent.replace(':', '').trim();
                        // strong 요소 내용을 제거한 후 텍스트 추출 (클론해서 처리)
                        const sectionClone = sectionElement.cloneNode(true);
                        sectionClone.querySelector('strong').remove();
                        const sectionContent = sectionClone.textContent.trim();

                        paper.summary.push({ type: sectionType, title: sectionTitle, content: sectionContent });
                    } else {
                        // strong 태그가 없는 경우 전체 내용을 저장
                        paper.summary.push({
                            type: sectionType,
                            title: sectionType.charAt(0).toUpperCase() + sectionType.slice(1),
                            content: sectionElement.textContent.trim()
                        });
                    }
                }
            }
        }

        // 점수 정보 파싱
        const scoreElements = paperElement.querySelectorAll('[class^="paper-"][class$="score"]');
        for (const scoreElement of scoreElements) {
            const scoreClass = scoreElement.getAttribute('class');
            const scoreType = scoreClass.replace('paper-', '').replace('-score', '');
            const scoreValue = scoreElement.getAttribute('data-value');
            if (scoreValue) {
                try {
                    paper.scores[scoreType] = parseFloat(scoreValue);
                } catch (e) {
                    paper.scores[scoreType] = scoreValue;
                }
            }
        }

        // 링크 정보 파싱
        const linkElement = paperElement.querySelector('.paper-link a');
        if (linkElement) {
            paper.link = linkElement.getAttribute('href') || '';
            // arXiv ID 추출
            const arxivMatch = paper.link.match(/arxiv\.org\/abs\/([\d\.]+)/);
            if (arxivMatch) {
                paper.arxiv_id = arxivMatch[1];
            }
        }

        // 태그 정보 파싱
        const tagsElement = paperElement.querySelector('.paper-tags');
        if (tagsElement) {
            const tagsAttr = tagsElement.getAttribute('data-tags');
            if (tagsAttr) {
                try {
                    paper.tags = JSON.parse(tagsAttr);
                } catch (e) {
                    // 데이터 속성에서 파싱 실패하면 텍스트에서 추출 시도
                    const tagsText = tagsElement.textContent.replace('📌 Tags:', '').trim();
                    paper.tags = tagsText.split(',').map(tag => tag.trim()).filter(tag => tag);
                }
            } else {
                // data-tags 속성이 없으면 텍스트에서 추출
                const tagsText = tagsElement.textContent.replace('📌 Tags:', '').trim();
                paper.tags = tagsText.split(',').map(tag => tag.trim()).filter(tag => tag);
            }
        }

        return paper;
    } catch (e) {
        console.error('Error parsing paper element:', e);
        return null;
    }
};

/**
 * DOM 요소에서 텍스트를 추출합니다.
 * @param {Element} element - DOM 요소 또는 null
 * @returns {string} 추출된 텍스트 또는 빈 문자열
 */
const getTextContent = (element) => {
    return element ? element.textContent.trim() : '';
};

/**
 * 보고서 내용의 형식을 감지합니다.
 * @param {string|Object} content - 보고서 내용
 * @returns {string} 감지된 형식 ('html', 'category', 'text', 'json', 'unknown')
 */
export const detectReportFormat = (content) => {
    // 이미 객체인 경우
    if (typeof content === 'object' && content !== null) {
        return 'json';
    }

    if (!content || typeof content !== 'string') {
        return 'unknown';
    }

    const trimmedContent = content.trim();

    // HTML 형식 감지 (태그 구조 확인)
    if (
        trimmedContent.startsWith('<div') ||
        trimmedContent.startsWith('<html') ||
        trimmedContent.startsWith('<!DOCTYPE')
    ) {
        // 표준화된 HTML 형식인지 확인 (특정 클래스 확인)
        if (
            trimmedContent.includes('class="report-header"') ||
            trimmedContent.includes('class="category"') ||
            trimmedContent.includes('class="paper"')
        ) {
            return 'standardized';
        }
        return 'html';
    }

    // JSON 형식 감지
    if (
        (trimmedContent.startsWith('{') && trimmedContent.endsWith('}')) ||
        (trimmedContent.startsWith('[') && trimmedContent.endsWith(']'))
    ) {
        try {
            JSON.parse(trimmedContent);
            return 'json';
        } catch (e) {
            // JSON 파싱 실패
        }
    }

    // <category> 태그 형식 감지
    if (
        trimmedContent.includes('<category categories=') &&
        trimmedContent.includes('</category>')
    ) {
        return 'category';
    }

    // 기타 텍스트로 간주
    return 'text';
};

/**
 * 보고서 내용을 표준 형식으로 변환합니다.
 * @param {Object} reportContent - 보고서 내용 객체
 * @returns {Object} 파싱 결과
 */
export const processReportContent = (reportContent) => {
    try {
        // 객체가 아닌 경우 처리
        if (typeof reportContent !== 'object' || reportContent === null) {
            return { error: 'Invalid report content' };
        }

        // 이미 파싱된 카테고리가 있으면 그대로 반환
        if (reportContent.parsed_categories) {
            return {
                parsed_categories: reportContent.parsed_categories,
                legacy_content: reportContent.legacy_content,
                title: reportContent.title
            };
        }

        // formatted_report 처리
        let content = '';
        if (reportContent.formatted_report) {
            content = reportContent.formatted_report;
        } else if (reportContent.content) {
            content = reportContent.content;
        } else if (typeof reportContent === 'string') {
            content = reportContent;
        }

        if (!content) {
            // papers 배열이 있으면 논문 데이터만 반환
            if (Array.isArray(reportContent.papers)) {
                return { papers: reportContent.papers, title: reportContent.title };
            }
            return { error: 'No content to process' };
        }

        // 형식 감지
        const format = detectReportFormat(content);

        // HTML 형식인 경우 파싱
        if (format === 'standardized' || format === 'html') {
            const parsedData = parseHtmlReport(content);
            return {
                parsed_data: parsedData,
                title: reportContent.title || (parsedData.header ? parsedData.header.title : ''),
                format: format
            };
        }

        // <category> 태그 형식인 경우 파싱
        if (format === 'category') {
            // 카테고리 태그 파싱
            const pattern = /<category categories=\'(.*?)\'>(.*?)<\/category>/gs;
            const matches = [...content.matchAll(pattern)];

            const categories = [];
            for (const match of matches) {
                try {
                    const cats = JSON.parse(match[1]);
                    const contentText = match[2].trim();
                    categories.push({ categories: cats, content: contentText });
                } catch (e) {
                    console.error('JSON parse error in categories:', e);
                    continue;
                }
            }

            return {
                parsed_categories: categories,
                title: reportContent.title,
                format: format
            };
        }

        // 그 외 형식은 레거시 콘텐츠로 처리
        return {
            legacy_content: content,
            title: reportContent.title,
            format: format
        };
    } catch (e) {
        console.error('Error processing report content:', e);
        return { error: e.message };
    }
};